/**
 * Green Brothers — canvas starfield.
 * White dots of varied sizes, shared viewport field and gentle parallax. No WebGL.
 */
(() => {
  if (customElements.get('gb-starfield')) return;

  const fields = new Set();
  const skies = new Set();
  let raf = 0;
  let lastT = 0;

  function syncSkyBackgrounds() {
    const sections = new Map();
    // Read all bounds before writing styles. This runs on layout changes only,
    // never while scrolling or on each animation frame.
    skies.forEach((field) => {
      const section = field.closest?.('.shopify-section');
      const background = field.parentElement;
      if (section && background) {
        sections.set(section, { background, rect: background.getBoundingClientRect() });
      }
    });
    const sibling = (section, direction) => {
      let next = section[direction];
      while (next && ['SCRIPT', 'STYLE', 'LINK'].includes(next.tagName)) next = next[direction];
      return next;
    };
    sections.forEach((first, section) => {
      if (sections.has(sibling(section, 'previousElementSibling'))) return;
      const group = [first];
      let next = sibling(section, 'nextElementSibling');
      while (sections.has(next)) {
        group.push(sections.get(next));
        next = sibling(next, 'nextElementSibling');
      }
      const height = group.at(-1).rect.bottom - first.rect.top;
      group.forEach(({ background, rect }) => {
        background.style.setProperty('--gb-sky-width', `${first.rect.width}px`);
        background.style.setProperty('--gb-sky-height', `${height}px`);
        background.style.setProperty('--gb-sky-x', `${first.rect.left - rect.left}px`);
        background.style.setProperty('--gb-sky-y', `${first.rect.top - rect.top}px`);
      });
    });
  }

  function mulberry32(seed) {
    let a = seed | 0;
    return () => {
      a = (a + 0x6d2b79f5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function loop(t) {
    raf = requestAnimationFrame(loop);
    const y = window.scrollY || 0;
    const dt = Math.min(32, t - lastT || 16);
    lastT = t;
    fields.forEach((field) => field.tick(t, dt, y));
  }

  function attach(field) {
    if (fields.has(field)) return;
    fields.add(field);
    if (fields.size === 1) {
      lastT = performance.now();
      raf = requestAnimationFrame(loop);
    }
  }

  function detach(field) {
    fields.delete(field);
    if (fields.size === 0 && raf) {
      cancelAnimationFrame(raf);
      raf = 0;
    }
  }

  class GbStarfield extends HTMLElement {
    connectedCallback() {
      if (this.#canvas) return;
      this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.#reduce = this.#motion.matches;
      this.#finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      this.#mobile = window.matchMedia('(max-width: 749px)').matches;

      this.#canvas = document.createElement('canvas');
      this.appendChild(this.#canvas);
      this.#ctx = this.#canvas.getContext('2d', { alpha: true });
      if (!this.#ctx) return;
      skies.add(this);

      this.#ptr.x = 0;
      this.#ptr.y = 0;
      this.#ptrTx = 0;
      this.#ptrTy = 0;

      this.#ro = new ResizeObserver(() => this.#resize());
      this.#ro.observe(this);

      this.#io = new IntersectionObserver(
        (entries) => {
          this.#visible = entries.some((e) => e.isIntersecting);
          if (this.#visible && this.#reduce) this.#drawStatic();
        },
        { rootMargin: '80px' }
      );
      this.#io.observe(this);

      this.#onPtr = (e) => {
        const r = this.getBoundingClientRect();
        if (
          e.clientX < r.left ||
          e.clientX > r.right ||
          e.clientY < r.top ||
          e.clientY > r.bottom
        ) {
          this.#ptrTx = 0;
          this.#ptrTy = 0;
          return;
        }
        this.#ptrTx = ((e.clientX - r.left) / r.width - 0.5) * 2;
        this.#ptrTy = ((e.clientY - r.top) / r.height - 0.5) * 2;
      };

      if (this.#finePointer && !this.#reduce) {
        window.addEventListener('pointermove', this.#onPtr, { passive: true });
      }

      document.addEventListener('visibilitychange', this.#onVis);
      this.#motion.addEventListener('change', this.#onMotion);

      this.#resize();
      if (this.#reduce) this.#drawStatic();
      else attach(this);
    }

    disconnectedCallback() {
      detach(this);
      skies.delete(this);
      syncSkyBackgrounds();
      this.#ro?.disconnect();
      this.#io?.disconnect();
      window.removeEventListener('pointermove', this.#onPtr);
      document.removeEventListener('visibilitychange', this.#onVis);
      this.#motion?.removeEventListener('change', this.#onMotion);
      this.#canvas?.remove();
      this.#canvas = null;
      this.#ctx = null;
    }

    #canvas;
    #ctx;
    #ro;
    #io;
    #motion;
    #stars = [];
    #visible = true;
    #reduce = false;
    #finePointer = false;
    #mobile = false;
    #w = 0;
    #h = 0;
    #vw = 0;
    #vh = 0;
    #dpr = 1;
    #ptr = { x: 0, y: 0 };
    #ptrTx = 0;
    #ptrTy = 0;
    #onPtr = () => {};
    #onMotion = ({ matches }) => {
      this.#reduce = matches;
      this.#ptr.x = this.#ptr.y = this.#ptrTx = this.#ptrTy = 0;
      window.removeEventListener('pointermove', this.#onPtr);
      if (matches) {
        detach(this);
        this.#drawStatic();
      } else {
        if (this.#finePointer) {
          window.addEventListener('pointermove', this.#onPtr, { passive: true });
        }
        if (!document.hidden) attach(this);
      }
    };
    #onVis = () => {
      if (document.hidden) detach(this);
      else if (!this.#reduce && this.isConnected) attach(this);
    };

    #resize() {
      const w = Math.max(1, this.clientWidth);
      const h = Math.max(1, this.clientHeight);
      const vw = Math.max(1, window.innerWidth);
      const vh = Math.max(1, window.innerHeight);
      this.#mobile = vw <= 749;
      const dpr = Math.min(this.#mobile ? 1.25 : 1.5, window.devicePixelRatio || 1);
      this.#w = w;
      this.#h = h;
      this.#vw = vw;
      this.#vh = vh;
      this.#dpr = dpr;
      this.#canvas.width = Math.round(w * dpr);
      this.#canvas.height = Math.round(h * dpr);
      this.#canvas.style.width = `${w}px`;
      this.#canvas.style.height = `${h}px`;
      this.#ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.#seed(vw, vh);
      syncSkyBackgrounds();
      if (this.#reduce) this.#drawStatic();
    }

    #seed(w, h) {
      const rand = mulberry32(0x5eed);
      const area = w * h;
      const farN = Math.round(Math.min(90, Math.max(12, area * 0.000042)));
      const midN = Math.round(Math.min(40, Math.max(6, area * 0.00002)));
      const nearN = Math.round(Math.min(16, Math.max(2, area * 0.000008)));
      const size = this.#mobile ? 0.8 : 1;

      const make = (n, layer) => {
        const out = [];
        for (let i = 0; i < n; i++) {
          out.push({
            x: rand(),
            y: rand(),
            r: (layer.r0 + rand() * layer.r1) * size,
            a: layer.a0 + rand() * layer.a1,
            phase: rand() * Math.PI * 2,
            tw: 0.25 + rand() * 0.5,
            depth: layer.depth,
          });
        }
        return out;
      };

      this.#stars = [
        ...make(farN, { r0: 0.9, r1: 0.9, a0: 0.9, a1: 0.1, depth: 0.12 }),
        ...make(midN, { r0: 1.6, r1: 1, a0: 0.95, a1: 0.05, depth: 0.38 }),
        ...make(nearN, { r0: 2.8, r1: 2.6, a0: 1, a1: 0, depth: 0.72 }),
      ];
    }

    tick(t, _dt, scrollY) {
      if (!this.#ctx || !this.#visible || document.hidden) return;
      this.#ptr.x += (this.#ptrTx - this.#ptr.x) * 0.06;
      this.#ptr.y += (this.#ptrTy - this.#ptr.y) * 0.06;
      this.#draw(t, scrollY);
    }

    #drawStatic() {
      this.#draw(0, 0, true);
      if (!this.#ctx || !this.#canvas) return;

      // Repeat the local static field over the full section, including tall pages.
      // Scrolling must not reveal areas outside the original viewport's drawing.
      const tileHeight = Math.min(this.#h, this.#vh);
      if (tileHeight < 1) return;
      const sourceHeight = Math.round(tileHeight * this.#dpr);
      for (let y = tileHeight; y < this.#h; y += tileHeight) {
        this.#ctx.drawImage(
          this.#canvas, 0, 0, this.#canvas.width, sourceHeight,
          0, y, this.#w, tileHeight
        );
      }
    }

    #draw(t, scrollY, frozen) {
      const ctx = this.#ctx;
      const w = this.#w;
      const h = this.#h;
      if (!ctx || w < 2 || h < 2) return;

      ctx.clearRect(0, 0, w, h);

      const rect = frozen ? { left: 0, top: 0 } : this.getBoundingClientRect();
      const vw = frozen ? w : this.#vw || window.innerWidth;
      const vh = frozen ? Math.min(h, this.#vh) : this.#vh || window.innerHeight;
      const time = frozen ? 0 : t * 0.001;

      const plot = (star) => {
        const depth = star.depth;
        const ox = frozen ? 0 :
          this.#ptr.x * 12 * depth + time * (1 + depth * 2);
        const oy = frozen ? 0 :
          -scrollY * depth +
          this.#ptr.y * 8 * depth +
          time * depth;
        const vx = ((star.x + ox / vw) % 1 + 1) % 1 * vw;
        const vy = ((star.y + oy / vh) % 1 + 1) % 1 * vh;
        const tw = frozen ? 1 : 0.96 + 0.04 * Math.sin(time * star.tw + star.phase);
        const a = star.a * tw;
        return { x: vx - rect.left, y: vy - rect.top, a };
      };

      for (const star of this.#stars) {
        const p = plot(star);
        ctx.beginPath();
        ctx.arc(p.x, p.y, star.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${p.a})`;
        ctx.fill();
      }
    }
  }

  customElements.define('gb-starfield', GbStarfield);
})();
